import { createContext, useContext, useState, useEffect } from "react";
import { apiLogin, apiGetMe, apiLogout, apiVerifyTwoFactor } from "../api";
import { apiGoogleLogin } from "../api/googleAuth";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token =
      localStorage.getItem("admin_token") ||
      sessionStorage.getItem("admin_token");
    if (token) {
      apiGetMe()
        .then((data) => {
          if (data.user && data.user.role === "admin") {
            setUser(data.user);
            setIsAuthenticated(true);
          } else {
            localStorage.removeItem("admin_token");
            sessionStorage.removeItem("admin_token");
          }
        })
        .catch(() => {
          localStorage.removeItem("admin_token");
          sessionStorage.removeItem("admin_token");
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const data = await apiLogin(email, password);
      if (data.requiresTwoFactor) return { ...data, success: false };
      if (data.user.role !== "admin") {
        return { success: false, message: "Access denied. Admin only." };
      }
      localStorage.removeItem("admin_token");
      sessionStorage.removeItem("admin_token");
      (rememberMe ? localStorage : sessionStorage).setItem(
        "admin_token",
        data.token,
      );
      setUser(data.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const googleLogin = async (rememberMe = true) => {
    try {
      const data = await apiGoogleLogin();
      if (data.requiresTwoFactor) return { ...data, success: false };
      if (!data.token || data.user?.role !== "admin") {
        return {
          success: false,
          message:
            "Access denied. This Google account is not an administrator.",
        };
      }
      localStorage.removeItem("admin_token");
      sessionStorage.removeItem("admin_token");
      (rememberMe ? localStorage : sessionStorage).setItem(
        "admin_token",
        data.token,
      );
      setUser(data.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      if (error.code === "auth/popup-closed-by-user") {
        return { success: false, message: "Google sign-in was cancelled." };
      }
      return {
        success: false,
        message: error.message || "Google sign-in failed.",
      };
    }
  };

  const verifyTwoFactor = async (challengeId, code, rememberMe = true) => {
    try {
      const data = await apiVerifyTwoFactor(challengeId, code);
      if (!data.token || data.user?.role !== "admin") {
        await apiLogout().catch(() => {});
        return { success: false, message: "Access denied. Admin only." };
      }
      localStorage.removeItem("admin_token");
      sessionStorage.removeItem("admin_token");
      (rememberMe ? localStorage : sessionStorage).setItem(
        "admin_token",
        data.token,
      );
      setUser(data.user);
      setIsAuthenticated(true);
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message };
    }
  };

  const logout = async () => {
    try {
      await apiLogout();
    } catch {
      // Local logout must still complete if the server is temporarily unavailable.
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      localStorage.removeItem("admin_token");
      sessionStorage.removeItem("admin_token");
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        googleLogin,
        verifyTwoFactor,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
